import { auth } from '../lib/firebase';

// Email template types
export type EmailTemplateType = 
  | 'userInvitation' 
  | 'passwordReset' 
  | 'notification' 
  | 'welcome' 
  | 'roleChanged' 
  | 'accountDeactivation' 
  | 'taskAssigned'
  | 'taskCompleted'
  | 'taskOverdue'
  | 'projectCreated'
  | 'projectUpdated'
  | 'formSubmitted'
  | 'appointmentCreated'
  | 'appointmentUpdated'
  | 'custom';

// Email service interface
export interface EmailData {
  to: string;
  templateType: EmailTemplateType;
  templateData: Record<string, any>;
  customSubject?: string;
  customMessage?: string;
  fromName?: string;
  replyTo?: string;
  organizationId?: string;
  credentialId?: string;  // SMTP credential ID (optional - falls back to Brevo)
}

export interface UserInvitationData {
  userEmail: string;
  userName: string;
  organizationName: string;
  userRole: string;
  organizationId: string;
  loginUrl?: string; // Optional, will be auto-filled with current origin
}

export interface NotificationData {
  to: string;
  subject: string;
  message: string;
  organizationName?: string;
  organizationId?: string;
  actionUrl?: string;
  actionText?: string;
  fromName?: string;
}

const EMAIL_SERVICE_URL = process.env.REACT_APP_EMAIL_SERVICE_URL || 'http://localhost:8080';

async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Authentication required to send emails');
  }
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
}

async function emailFetch(endpoint: string, body: Record<string, any>): Promise<any> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${EMAIL_SERVICE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`);
    }

    return data;
  } catch (error: any) {
    console.error(`Email service error (${endpoint}):`, error);
    throw new Error(error.message || 'Email service request failed');
  }
}

/**
 * Universal email sending service
 * Uses the standalone email-service (Python/Flask on Cloud Run) instead of Firebase Functions.
 * System-level notifications use Brevo SMTP fallback (no credentials needed).
 * Organization-specific emails can pass credentialId for custom SMTP.
 */
export const emailService = {
  /**
   * Send email with custom template
   */
  async sendEmail(data: EmailData): Promise<any> {
    return emailFetch('/api/send-email', data);
  },

  /**
   * Send user invitation email
   */
  async sendUserInvitation(data: UserInvitationData): Promise<any> {
    const dataWithUrl = {
      ...data,
      loginUrl: data.loginUrl || window.location.origin
    };
    return emailFetch('/api/send-user-invitation', dataWithUrl);
  },

  /**
   * Send notification email
   * System notifications (no organizationId) use Brevo fallback - no credentials needed.
   * Org-specific notifications use org's default SMTP credential if configured.
   */
  async sendNotification(data: NotificationData): Promise<any> {
    return emailFetch('/api/send-notification', data);
  },

  /**
   * Send test email
   */
  async sendTestEmail(to: string, organizationId?: string, credentialId?: string): Promise<any> {
    return emailFetch('/api/send-test-email', { to, organizationId, credentialId });
  },

  /**
   * Send welcome email to new user
   */
  async sendWelcomeEmail(userEmail: string, userName: string, organizationName: string, organizationId?: string): Promise<any> {
    return this.sendNotification({
      to: userEmail,
      subject: 'Üdvözöljük a YouCRM rendszerben!',
      message: `Kedves ${userName}!<br><br>
        Üdvözöljük a <strong>${organizationName}</strong> YouCRM rendszerében!<br><br>
        Fiókja sikeresen aktiválva lett. Most már hozzáférhet a rendszer összes funkciójához, amelyre jogosultsága van.<br><br>
        Ha bármilyen kérdése van, keressen minket bizalommal.`,
      organizationName,
      organizationId,
      actionUrl: window.location.origin,
      actionText: 'Bejelentkezés',
      fromName: organizationName
    });
  },

  /**
   * Send account activation email
   */
  async sendAccountActivation(userEmail: string, userName: string, organizationName: string, organizationId?: string): Promise<any> {
    return this.sendNotification({
      to: userEmail,
      subject: 'Fiók aktiválva',
      message: `Kedves ${userName}!<br><br>
        Fiókját sikeresen aktiváltuk a <strong>${organizationName}</strong> YouCRM rendszerében.<br><br>
        Most már teljes hozzáféréssel rendelkezik a rendszerhez.`,
      organizationName,
      organizationId,
      actionUrl: window.location.origin,
      actionText: 'Bejelentkezés',
      fromName: organizationName
    });
  },

  /**
   * Send role changed notification
   */
  async sendRoleChangedNotification(
    userEmail: string, 
    userName: string, 
    organizationName: string, 
    oldRole: string, 
    newRole: string,
    organizationId?: string
  ): Promise<any> {
    const roleNames = {
      'admin': 'Adminisztrátor',
      'user': 'Felhasználó',
      'super_admin': 'Szuper Adminisztrátor'
    };

    return this.sendNotification({
      to: userEmail,
      subject: 'Jogosultságok módosítva',
      message: `Kedves ${userName}!<br><br>
        A <strong>${organizationName}</strong> rendszerében az Ön jogosultságai módosultak.<br><br>
        Korábbi szerepkör: <strong>${roleNames[oldRole as keyof typeof roleNames] || oldRole}</strong><br>
        Új szerepkör: <strong>${roleNames[newRole as keyof typeof roleNames] || newRole}</strong><br><br>
        A változások azonnal érvénybe léptek.`,
      organizationName,
      organizationId,
      actionUrl: window.location.origin,
      actionText: 'Bejelentkezés',
      fromName: organizationName
    });
  },

  /**
   * Send account deactivation email
   */
  async sendAccountDeactivation(userEmail: string, userName: string, organizationName: string, organizationId?: string): Promise<any> {
    return this.sendNotification({
      to: userEmail,
      subject: 'Fiók deaktiválva',
      message: `Kedves ${userName}!<br><br>
        Tájékoztatjuk, hogy fiókja deaktiválásra került a <strong>${organizationName}</strong> YouCRM rendszerében.<br><br>
        Ha úgy gondolja, hogy ez tévedés, kérjük vegye fel a kapcsolatot a rendszergazdával.`,
      organizationName,
      organizationId,
      fromName: organizationName
    });
  }
};

export default emailService;